import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import useLocale from 'i18n/useLocale';
import { LANGUAGES, LOCALES_ISO_639_1 } from 'components/i18n/locales';
import TranslateIcon from '@mui/icons-material/Translate';
import { Badge } from '@mui/material';

// ----------------------------------------------------------------------

export type LanguagePopoverProps = IconButtonProps & {
  data?: {
    value: string;
    label: string;
    icon: string;
  }[];
};

export function LanguagePopover({ data = [], sx, ...other }: LanguagePopoverProps) {
  const { changeLocale, languagesAvailable, locale } = useLocale();
  //const [locale, setLocale] = useState<string>(data[0].value);

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const handleChangeLang = useCallback((newLocale: LOCALES_ISO_639_1) => {
      changeLocale(newLocale);
      handleClosePopover();
    },[handleClosePopover]
  );

  const currentLang = data.find((lang) => lang.value === locale);

  const renderFlag = (label?: string, icon?: string) => (
    <Box
      component="img"
      alt={label}
      src={icon}
      sx={{ width: 26, height: 20, borderRadius: 0.5, objectFit: 'cover' }}
    />
  );

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          width: 40,
          height: 40,
          ...(openPopover && { bgcolor: 'action.selected' }),
          ...sx,
        }}
        {...other}
        >
        <Badge badgeContent={locale?.toUpperCase()} color="error">
          <TranslateIcon/>
        </Badge>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuList
          disablePadding
          sx={{
            p: 0.5,
            gap: 0.5,
            width: 160,
            display: 'flex',
            flexDirection: 'column',
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              [`&.${menuItemClasses.selected}`]: {
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightSemiBold',
              },
            },
          }}
        >
          {languagesAvailable.map((lang) => {
            // Extract ISO 639-1 code from locale (e.g., 'en-US' -> 'en')
            const langCode = lang.split('-')[0] as LOCALES_ISO_639_1;
            const language = LANGUAGES[langCode];
            
            // Skip if language definition not found
            if (!language) return null;
            
            return ( 
              <MenuItem
                key={`${lang}-option`}
                onClick={() => lang !== locale && handleChangeLang(lang)}
                selected={lang === locale}
                sx={{
                  color: lang === locale ? 'text.primary' : 'text.secondary',
                }}
              >
                {`${language.name} (${lang})`}
              </MenuItem>
            );
          })}

        </MenuList>
      </Popover>
    </>
  );
}
